import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Sparkles, Palette, Image as ImageIcon, Brain, BarChart3,
  ShieldCheck, LogOut, Menu, Globe, Link2, Mail, Inbox as InboxIcon, Film,
  Megaphone, LifeBuoy, CreditCard, ClipboardCheck, Calendar as CalendarIcon,
  Settings as SettingsIcon, Zap, Plug, Briefcase, Search as SearchIcon, Crown,
  Share2, Sun, Activity, Users, MessageSquare, ChevronDown, Wand2,
} from "lucide-react";

/**
 * Categorised sidebar:
 *   - Sections collapse/expand (state persisted in localStorage)
 *   - Nav area is independently scrollable (`overflow-y-auto`) so users on
 *     short screens can always reach the bottom items
 *   - Footer (user + logout) sticks at the bottom — never disappears
 *   - Admin-only sections are filtered server-side by role
 */

const SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    items: [
      { to: "/dashboard",              icon: LayoutDashboard, label: "Overview" },
      { to: "/dashboard/brief",        icon: Sun,             label: "Morning Brief" },
      { to: "/dashboard/brand-health", icon: Activity,        label: "Brand Health" },
      { to: "/dashboard/analytics",    icon: BarChart3,       label: "Analytics" },
    ],
  },
  {
    id: "ai-create",
    label: "AI Create",
    items: [
      { to: "/dashboard/magic",    icon: Wand2,     label: "Magic Tab · One-click" },
      { to: "/dashboard/content",  icon: Sparkles,  label: "Content Studio" },
      { to: "/dashboard/logos",    icon: ImageIcon, label: "Logo Studio" },
      { to: "/dashboard/brand",    icon: Palette,   label: "Brand Identity" },
      { to: "/dashboard/website",  icon: Globe,     label: "Website Builder" },
      { to: "/dashboard/video",    icon: Film,      label: "Video & Creatives" },
      { to: "/dashboard/analysis", icon: Brain,     label: "Business Analysis" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing & Ads",
    items: [
      { to: "/dashboard/power-hour",       icon: Zap,        label: "Power Hour" },
      { to: "/dashboard/one-click",        icon: Megaphone,  label: "One-click Campaign" },
      { to: "/dashboard/campaign-manager", icon: Megaphone,  label: "Campaign Manager" },
      { to: "/dashboard/ads",              icon: Megaphone,  label: "Ad Push (Meta/Google)" },
      { to: "/dashboard/calendar",         icon: CalendarIcon, label: "Post Calendar" },
      { to: "/dashboard/campaigns",        icon: Mail,       label: "Email Campaigns" },
      { to: "/dashboard/whatsapp",         icon: MessageSquare, label: "WhatsApp Broadcasts" },
    ],
  },
  {
    id: "growth",
    label: "Growth & Engage",
    items: [
      { to: "/dashboard/crm",           icon: Briefcase,  label: "CRM" },
      { to: "/dashboard/seo",           icon: SearchIcon, label: "SEO Studio" },
      { to: "/dashboard/inbox",         icon: InboxIcon,  label: "Reply Inbox" },
      { to: "/dashboard/share-preview", icon: Share2,     label: "Share Preview" },
    ],
  },
  {
    id: "accounts",
    label: "Accounts & Billing",
    items: [
      { to: "/dashboard/connect",    icon: Link2,      label: "Connections" },
      { to: "/dashboard/billing",    icon: CreditCard, label: "Billing" },
      { to: "/dashboard/whitelabel", icon: Crown,      label: "White-label" },
      { to: "/dashboard/support",    icon: LifeBuoy,   label: "Support" },
      { to: "/dashboard/settings",   icon: SettingsIcon, label: "Settings" },
    ],
  },
];

const ADMIN_SECTION = {
  id: "admin",
  label: "Administration",
  adminOnly: true,
  items: [
    { to: "/admin",               icon: ShieldCheck,     label: "Admin Console" },
    { to: "/admin/customers",     icon: Users,           label: "Customers" },
    { to: "/admin/plans",         icon: Crown,           label: "Plans & Pricing" },
    { to: "/admin/magic",         icon: Wand2,           label: "Magic Console" },
    { to: "/admin/integrations",  icon: Plug,            label: "Integrations" },
    { to: "/admin/approvals",     icon: ClipboardCheck,  label: "Approvals" },
    { to: "/admin/tickets",       icon: LifeBuoy,        label: "All Tickets" },
  ],
};

const STORAGE_KEY = "myb_sidebar_collapsed";

function useCollapsedSections() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch (_e) { /* storage disabled */ }
      return next;
    });
  };
  return [collapsed, toggle];
}

function NavItem({ to, icon: Icon, label, end = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
          isActive
            ? "bg-white/5 text-white border border-white/10"
            : "text-white/60 hover:text-white hover:bg-white/[0.03] border border-transparent"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function Section({ section, collapsed, onToggle, isAdmin }) {
  if (section.adminOnly && !isAdmin) return null;
  const isCollapsed = collapsed.has(section.id);
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => onToggle(section.id)}
        data-testid={`sidebar-section-${section.id}`}
        className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white/70 transition-colors"
      >
        <span>{section.label}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform duration-200 ${isCollapsed ? "-rotate-90" : ""}`}
        />
      </button>
      {!isCollapsed && (
        <div className="space-y-0.5 mt-1">
          {section.items.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
              end={item.to === "/dashboard"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useCollapsedSections();
  const isAdmin = user?.role && user.role !== "client";

  const Sidebar = (
    <aside
      className="flex flex-col h-full w-72 bg-[#0A0A10] border-r border-white/5"
      data-testid="dashboard-sidebar"
    >
      {/* Brand — fixed top */}
      <div className="shrink-0 px-5 pt-5 pb-3">
        <Link to="/dashboard" data-testid="sidebar-brand-link" className="flex items-center">
          <Logo size="h-12" hideText />
        </Link>
      </div>

      {/* Scrollable nav body */}
      <nav
        className="flex-1 overflow-y-auto px-3 py-2 sidebar-scroll"
        data-testid="sidebar-nav-scroll"
      >
        {SECTIONS.map((section) => (
          <Section
            key={section.id}
            section={section}
            collapsed={collapsed}
            onToggle={toggleCollapsed}
            isAdmin={isAdmin}
          />
        ))}
        {isAdmin && (
          <Section
            section={ADMIN_SECTION}
            collapsed={collapsed}
            onToggle={toggleCollapsed}
            isAdmin={isAdmin}
          />
        )}
      </nav>

      {/* Footer — fixed bottom */}
      <div className="shrink-0 border-t border-white/5 p-4">
        <div className="px-1 mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Signed in as</div>
          <div className="text-sm text-white truncate" data-testid="sidebar-user-name">
            {user?.name}
          </div>
          <div className="text-[11px] font-mono text-white/40 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5 transition-all"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex">
      {/* desktop sidebar */}
      <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="bg-black/70 absolute inset-0" onClick={() => setOpen(false)} />
          <div className="relative h-full">{Sidebar}</div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 glass-strong border-b border-white/5 px-4 py-3 flex items-center justify-between">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(true)}
            className="p-2 rounded-lg hover:bg-white/5"
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/dashboard" className="flex items-center">
            <Logo size="h-9" hideText />
          </Link>
          <div className="w-9" />
        </header>
        <div className="p-6 lg:p-10 grid-bg min-h-screen">{children}</div>
      </main>
    </div>
  );
}
